import type { PdfValidationOptions, ValidatedPdfHeader } from "./file-validation";
import { validatePdfBlob } from "./file-validation";

export interface CryptoDigest {
  subtle: Pick<SubtleCrypto, "digest">;
}

export type IdentifiedPdfFile = ValidatedPdfHeader & {
  fileName: string | null;
  sha256: string;
  arrayBuffer: ArrayBuffer;
  bytes: Uint8Array<ArrayBuffer>;
};

export function bytesToLowercaseHex(bytes: Uint8Array): string {
  let result = "";
  for (const byte of bytes) {
    result += byte.toString(16).padStart(2, "0");
  }
  return result;
}

function getCrypto(): CryptoDigest {
  if (globalThis.crypto?.subtle === undefined) {
    throw new Error("This browser does not provide the Web Crypto API.");
  }

  return globalThis.crypto;
}

function digestSource(data: ArrayBuffer | Uint8Array): ArrayBuffer {
  if (data instanceof ArrayBuffer) {
    return data;
  }

  if (
    data.buffer instanceof ArrayBuffer &&
    data.byteOffset === 0 &&
    data.byteLength === data.buffer.byteLength
  ) {
    return data.buffer;
  }

  return data.slice().buffer;
}

export async function sha256Hex(
  data: ArrayBuffer | Uint8Array,
  cryptoImplementation: CryptoDigest = getCrypto(),
): Promise<string> {
  const digest = await cryptoImplementation.subtle.digest(
    "SHA-256",
    digestSource(data),
  );
  return bytesToLowercaseHex(new Uint8Array(digest));
}

export async function hashBlobSha256(
  blob: Blob,
  cryptoImplementation: CryptoDigest = getCrypto(),
): Promise<string> {
  return sha256Hex(await blob.arrayBuffer(), cryptoImplementation);
}

function blobFileName(blob: Blob): string | null {
  if (
    "name" in blob &&
    typeof (blob as Blob & { name?: unknown }).name === "string"
  ) {
    return (blob as Blob & { name: string }).name;
  }

  return null;
}

export async function identifyPdfFile(
  blob: Blob,
  options: PdfValidationOptions & {
    crypto?: CryptoDigest;
  } = {},
): Promise<IdentifiedPdfFile> {
  const validation = await validatePdfBlob(blob, options);
  const arrayBuffer = await blob.arrayBuffer();
  const sha256 = await sha256Hex(arrayBuffer, options.crypto ?? getCrypto());

  return {
    ...validation,
    fileName: blobFileName(blob),
    sha256,
    arrayBuffer,
    bytes: new Uint8Array(arrayBuffer),
  };
}
